import type {
	Column,
	ColumnOrderState,
	ColumnPinningState,
	RowData,
	VisibilityState
} from '@tanstack/react-table'
import type { Dispatch, ReactNode, SetStateAction } from 'react'
import type { VirtualThreshold } from '#hooks/use-virtualization'

export interface DataTableColumnMenuRenderProps<TData> {
	column: Column<TData, unknown>
	label: string
	/** Built-in items (sort/pin/autosize/hide). Spread to compose your own list. */
	internalItems: ReactNode[]
	/** Imperatively close the menu, e.g. after an async custom action. */
	closeMenu: () => void
}

/** Returns the full item list for a column's actions menu; receives the built-in items to compose. */
export type DataTableColumnMenuRender<TData> = (
	props: DataTableColumnMenuRenderProps<TData>
) => ReactNode

declare module '@tanstack/react-table' {
	interface ColumnMeta<TData extends RowData, TValue> {
		label?: string
		description?: string
		enableColumnMenu?: boolean
		renderColumnMenuItems?: DataTableColumnMenuRender<TData>
		className?: string
	}
}

export interface DataTablePaginationConfig {
	pageIndex: number
	pageCount: number
	onPageChange: (pageIndex: number) => void
	pageSize?: number
	onPageSizeChange?: (pageSize: number) => void
	pageSizeOptions?: number[]
}

/** Reserved id for the auto-injected checkbox column when selection is enabled. */
export const SELECT_COLUMN_ID = '__data_table_select__'

export interface DataTableSelectionConfig<TData> {
	/** Controlled set of selected row ids; owned by the parent. */
	selectedIds: Set<string>
	onSelectionChange: (next: Set<string>) => void
	/** Render the header "select all" checkbox (scoped to the current page). Default true. */
	enableSelectAll?: boolean
	/** Rows for which this returns false cannot be selected. */
	isRowSelectable?: (row: TData) => boolean
	/** Tooltip on a row's disabled checkbox when `isRowSelectable` returns false. */
	notSelectableTooltip?: ReactNode | ((row: TData) => ReactNode)
}

/**
 * Empty-state content. A bare string is treated as the title; the object form
 * fills the illustrated empty block (icon, title, hint, and an optional action).
 */
export interface DataTableEmptyConfig {
	icon?: ReactNode
	title: ReactNode
	description?: ReactNode
	action?: ReactNode
}

export interface DataTableStateConfig {
	loading?: boolean | ReactNode
	loadingRows?: number
	empty?: string | DataTableEmptyConfig
	error?: boolean | ReactNode
}

export interface DataTableLoadMoreConfig {
	onLoadMore: () => unknown
	hasMore?: boolean
	loading?: boolean
	threshold?: VirtualThreshold
}

export interface DataTableSort<TSortKey extends string = string> {
	by?: TSortKey
	direction?: 'asc' | 'desc'
}

export interface DataTableSortConfig<TSortKey extends string = string> {
	state: DataTableSort<TSortKey>
	onSortChange: (next: DataTableSort<TSortKey>) => void
}

export interface DataTableColumnState {
	visibility: VisibilityState
	order: ColumnOrderState
	pinning: ColumnPinningState
}

export interface DataTableColumnStateConfig {
	value: DataTableColumnState
	onChange: Dispatch<SetStateAction<DataTableColumnState>>
}

export interface DataTableColumn<TData, TSortKey extends string = string> {
	id: string
	header: ReactNode
	label?: string
	/** Explanation shown under the column name in the header tooltip. */
	description?: string
	cell: (dataItem: TData) => ReactNode
	width?: number
	minWidth?: number
	maxWidth?: number
	enableResizing?: boolean
	flex?: number
	/** Sort key reported on sort change. Setting it makes the header sortable. */
	sortKey?: TSortKey
	hide?: boolean
	hideable?: boolean
	pinned?: 'left' | 'right'
	/** When false, the column cannot be pinned from the header actions menu (`getCanPin()` is false). Defaults to true. */
	pinnable?: boolean
	/** When false, the column is excluded from the column settings popover and cannot be reordered, hidden, or pinned there. Independent of the header actions menu. */
	manageable?: boolean
	/** When false, hides this column's header actions menu. Defaults to the table-level `enableColumnMenu` (true). */
	enableColumnMenu?: boolean
	/** Takes over this column's actions menu items; overrides the table-level `renderColumnMenuItems`. */
	renderColumnMenuItems?: DataTableColumnMenuRender<TData>
	/**
	 * Classes added to this column's header and body cells, e.g. a column tint.
	 * Keep it static: body cells re-render only when data or columns change, so a
	 * class driven by other state reaches the header alone.
	 */
	className?: string
}

export function getColumnLabel<TData>(column: DataTableColumn<TData>): string {
	if (column.label !== undefined) {
		return column.label
	}
	if (typeof column.header === 'string') {
		return column.header
	}
	return humanizeColumnId(column.id)
}

function humanizeColumnId(id: string): string {
	return id
		.replace(/([a-z0-9])([A-Z])/g, '$1 $2')
		.replace(/[_-]+/g, ' ')
		.replace(/\b\w/g, (char) => char.toUpperCase())
}

export function makeInitialColumnState<TData>(
	columns: DataTableColumn<TData>[]
): DataTableColumnState {
	const visibility: VisibilityState = {}
	const leftPinned: string[] = []
	const rightPinned: string[] = []
	for (const column of columns) {
		if (column.hide === true) {
			visibility[column.id] = false
		}
		if (column.pinned === 'left') {
			leftPinned.push(column.id)
		} else if (column.pinned === 'right') {
			rightPinned.push(column.id)
		}
	}
	return {
		visibility,
		order: [],
		pinning: { left: leftPinned, right: rightPinned }
	}
}
